import { useState } from "react";
import { Accordion, Table, Spinner } from "@heroui/react";
import type { HistoryItem, CampaignActionLogEntry } from "@/entities/campaign";
import { ActionDetailsModal } from "./ActionDetailsModal";

export interface CampaignHistoryProps {
  campaignId?: string | number;
  actionLogs?: CampaignActionLogEntry[];
  history?: HistoryItem[];
  isLoading?: boolean;
}

export const CampaignHistory = ({
  campaignId,
  actionLogs,
  history,
  isLoading,
}: CampaignHistoryProps) => {
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);

  const isActionLogsMode = actionLogs !== undefined;
  const count = isActionLogsMode ? actionLogs.length : history?.length || 0;

  const hasOldValues =
    !isActionLogsMode &&
    Boolean(history?.some((item) => item.oldValue && item.oldValue !== "—"));

  const colSpan = hasOldValues ? 5 : 4;

  return (
    <>
      <Accordion variant="surface">
        <Accordion.Item id="history">
          <Accordion.Heading>
            <Accordion.Trigger className="text-base font-semibold">
              Журнал изменений
              <Accordion.Indicator />
            </Accordion.Trigger>
          </Accordion.Heading>
          <Accordion.Panel>
            <Accordion.Body>
              <Table>
                <Table.ScrollContainer>
                  <Table.Content aria-label="Журнал изменений">
                    <Table.Header>
                      <Table.Column isRowHeader className="w-36">
                        Дата
                      </Table.Column>
                      <Table.Column className="w-32">Автор</Table.Column>
                      <Table.Column className="w-48">Действие</Table.Column>
                      {hasOldValues && (
                        <Table.Column className="w-28">Было</Table.Column>
                      )}
                      <Table.Column>
                        {hasOldValues ? "Стало / Результат" : "Результат"}
                      </Table.Column>
                    </Table.Header>
                    <Table.Body>
                      {isLoading ? (
                        <Table.Row>
                          <Table.Cell
                            colSpan={colSpan}
                            className="text-center py-6 text-neutral-400 text-sm"
                          >
                            <div className="flex items-center justify-center gap-2">
                              <Spinner size="sm" />
                              <span>Загрузка журнала изменений...</span>
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      ) : count === 0 ? (
                        <Table.Row>
                          <Table.Cell
                            colSpan={colSpan}
                            className="text-center py-6 text-neutral-400 text-sm"
                          >
                            Изменений по кампании пока не зафиксировано
                          </Table.Cell>
                        </Table.Row>
                      ) : isActionLogsMode ? (
                        actionLogs.map((log) => {
                          const dateFormatted = new Date(
                            log.created_at
                          ).toLocaleString("ru-RU", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          });
                          const isAutopilot =
                            log.event_type === "MODULE_EXECUTION";

                          return (
                            <Table.Row key={log.id}>
                              <Table.Cell className="whitespace-nowrap text-xs text-neutral-400">
                                {dateFormatted}
                              </Table.Cell>
                              <Table.Cell className="whitespace-nowrap font-medium text-xs">
                                <span
                                  className={
                                    isAutopilot
                                      ? "text-primary font-semibold"
                                      : "text-foreground"
                                  }
                                >
                                  {isAutopilot ? "Автопилот" : "Пользователь"}
                                </span>
                              </Table.Cell>
                              <Table.Cell className="whitespace-nowrap text-xs font-medium">
                                <div className="flex flex-col gap-0.5">
                                  <div className="flex items-center gap-1.5">
                                    <span>{log.title}</span>
                                    {log.entities_affected > 0 && (
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-400 font-semibold border border-rose-500/20">
                                        -{log.entities_affected}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedActionId(log.id)}
                                    className="text-[11px] text-accent hover:underline text-left cursor-pointer transition-colors p-0 bg-transparent border-0 self-start"
                                  >
                                    Подробнее
                                  </button>
                                </div>
                              </Table.Cell>
                              <Table.Cell className="text-xs text-foreground">
                                {log.description}
                              </Table.Cell>
                            </Table.Row>
                          );
                        })
                      ) : (
                        history?.map((item) => (
                          <Table.Row key={item.id}>
                            <Table.Cell className="whitespace-nowrap text-xs text-neutral-400">
                              {item.date}
                            </Table.Cell>
                            <Table.Cell className="whitespace-nowrap font-medium text-xs">
                              <span
                                className={
                                  item.author === "Автопилот"
                                    ? "text-primary font-semibold"
                                    : "text-foreground"
                                }
                              >
                                {item.author}
                              </span>
                            </Table.Cell>
                            <Table.Cell className="whitespace-nowrap text-xs">
                              {item.action}
                            </Table.Cell>
                            {hasOldValues && (
                              <Table.Cell className="text-xs text-neutral-400">
                                {item.oldValue}
                              </Table.Cell>
                            )}
                            <Table.Cell className="text-xs text-foreground font-medium">
                              {item.newValue}
                            </Table.Cell>
                          </Table.Row>
                        ))
                      )}
                    </Table.Body>
                  </Table.Content>
                </Table.ScrollContainer>
              </Table>
            </Accordion.Body>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {/* Модальное окно детализации действия */}
      {selectedActionId && campaignId && (
        <ActionDetailsModal
          campaignId={campaignId}
          actionId={selectedActionId}
          onClose={() => setSelectedActionId(null)}
        />
      )}
    </>
  );
};
